import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { workspace } from "../lib/workspace";
import type { Protection, ConfiguredTemplate, Sample } from "../lib/workspace";
import { useApi } from "../lib/useApi";
import { useAction } from "./ProjectTools";
import { Button, CardSkeleton } from "../components/ui";
const StatusBadge = ({ status }: { status: string }) => (
  <span className="platform-role">{status.replaceAll("_", " ")}</span>
);
import { birr, money, relativeTime } from "../lib/format";

const size = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;

export function ProtectionCenter() {
  const { data, error, reload } = useApi(
    () => workspace.protection(),
    [],
    30_000,
  );
  const restores = useApi(() => workspace.restores(), [], 10_000);
  return (
    <>
      <header className="page-head">
        <div>
          <p className="platform-eyebrow">DATABASE PROTECTION</p>
          <h1>Keep a recoverable copy</h1>
          <p className="subtitle">
            Backup coverage, retention, and recovery in one place.
          </p>
        </div>
        <Button
          onClick={() => {
            reload();
            restores.reload();
          }}
        >
          Refresh
        </Button>
      </header>
      {error && <p className="alert error">{error}</p>}
      {!data && !error && <CardSkeleton />}
      {data && (
        <section className="workspace-metrics">
          <div>
            <span className="platform-role">Protected</span>
            <strong>
              {data.filter((d) => d.protection === "protected").length}
            </strong>
          </div>
          <div>
            <span className="platform-role">Needs attention</span>
            <strong>
              {data.filter((d) => d.protection === "overdue").length}
            </strong>
          </div>
          <div>
            <span className="platform-role">Stored backups</span>
            <strong>
              {size(data.reduce((n, d) => n + d.backup_bytes, 0))}
            </strong>
          </div>
          <div>
            <span className="platform-role">Database allocation</span>
            <strong>
              {data.reduce((n, d) => n + d.allocated_storage_gb, 0)} GB
            </strong>
          </div>
        </section>
      )}
      {data?.length === 0 && (
        <div className="empty">
          <h2>Protect your first database</h2>
          <Link className="btn primary" to="/databases">
            Create a database
          </Link>
        </div>
      )}
      <div className="stack">
        {data?.map((d) => (
          <ProtectionCard
            database={d}
            key={d.id}
            reload={() => {
              reload();
              restores.reload();
            }}
          />
        ))}
      </div>
      <section className="card" style={{ marginTop: 24 }}>
        <h2>Restore jobs</h2>
        <p className="muted">
          Each restore creates a separate database on the source plan. Its
          subscription is billed under your normal wallet and trial rules. The
          source stays unchanged.
        </p>
        {restores.error && <p className="alert error">{restores.error}</p>}
        {restores.data?.length === 0 && (
          <p className="tiny muted">No restore copies requested.</p>
        )}
        {restores.data?.map((r) => (
          <div className="workspace-dependency" key={r.id}>
            <div>
              <Link to={`/databases/${r.target_id}`}>Open restored copy →</Link>
              <p className="tiny muted">
                Requested {relativeTime(r.created_at)}
              </p>
              {r.error && <p className="platform-failure">{r.error}</p>}
            </div>
            <StatusBadge status={r.status} />
          </div>
        ))}
      </section>
    </>
  );
}

function ProtectionCard({
  database: d,
  reload,
}: {
  database: Protection;
  reload: () => void;
}) {
  const [policy, setPolicy] = useState<Protection["policy"] | null>(null);
  const value = policy ?? d.policy;
  const [restore, setRestore] = useState(false),
    [name, setName] = useState(`${d.name}-restore`.slice(0, 64));
  const [confirmed, setConfirmed] = useState(false);
  const { busy, run, feedback } = useAction(reload);
  return (
    <article className="card stack">
      <div className="row">
        <div>
          <p className="platform-role">
            {d.engine} · {d.plan}
          </p>
          <h2>
            <Link to={`/databases/${d.id}`}>{d.name}</Link>
          </h2>
        </div>
        <span className="grow" />
        <span
          className={`workspace-verdict ${d.protection === "protected" ? "ready" : "blocked"}`}
        >
          {d.protection}
        </span>
      </div>
      {feedback}
      <p className="muted">
        Last successful backup:{" "}
        {d.last_success_at
          ? new Date(d.last_success_at).toLocaleString()
          : "No successful backup yet"}
        . {size(d.backup_bytes)} stored.
      </p>
      {d.latest_error && <p className="platform-failure">{d.latest_error}</p>}
      {d.protection !== "unsupported" && (
        <form
          className="advanced-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await workspace.saveProtection(d.id, value);
              setPolicy(null);
            }, "Backup policy saved");
          }}
        >
          <label className="row">
            <input
              type="checkbox"
              checked={value.enabled}
              onChange={(e) =>
                setPolicy({ ...value, enabled: e.target.checked })
              }
            />
            Scheduled backups
          </label>
          <label>
            Interval (hours)
            <input
              type="number"
              min={1}
              max={168}
              value={value.interval_hours}
              onChange={(e) =>
                setPolicy({ ...value, interval_hours: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Retention (days)
            <input
              type="number"
              min={1}
              max={90}
              value={value.retention_days}
              onChange={(e) =>
                setPolicy({ ...value, retention_days: Number(e.target.value) })
              }
            />
          </label>
          <Button type="submit" loading={busy}>
            Save policy
          </Button>
        </form>
      )}
      <p className="tiny muted">
        The scheduler checks hourly. Reducing retention allows older backups to
        be removed. Backups used by restore copies remain retained while the
        copy exists.
      </p>
      <div className="row">
        <Link className="btn" to={`/databases/${d.id}`}>
          Backup history & downloads
        </Link>
        {d.protection !== "unsupported" && (
          <Button
            disabled={busy || d.status !== "available"}
            onClick={() =>
              void run(async () => {
                const result = await api.createBackup(d.id);
                if (result.status === "failed")
                  throw new Error(result.error_message || "Backup failed");
              }, "Backup completed")
            }
          >
            Back up now
          </Button>
        )}
        {d.engine === "postgres" && d.latest_backup_id && (
          <Button disabled={busy} onClick={() => setRestore(!restore)}>
            Restore latest to a copy
          </Button>
        )}
      </div>
      {restore && (
        <form
          className="workspace-review stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (!d.latest_backup_id || !confirmed) return;
            void run(async () => {
              await workspace.restoreCopy(d.id, d.latest_backup_id!, name);
              setRestore(false);
            }, "Restore copy queued. Track progress in Restore jobs.");
          }}
        >
          <h3>Create a separate restored database</h3>
          <p>
            The copy uses the <strong>{d.plan}</strong> plan and{" "}
            {d.allocated_storage_gb} GB allocation. A new subscription may be
            charged. Your source database and application connections stay
            unchanged.
          </p>
          <label>
            Copy name
            <input
              required
              maxLength={64}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="row">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            I understand this creates a separately billed database.
          </label>
          <div className="row">
            <Button type="submit" loading={busy} disabled={!confirmed}>
              Create restored copy
            </Button>
            <Button type="button" onClick={() => setRestore(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </article>
  );
}

function Sparkline({
  history,
  metric,
}: {
  history: Sample[];
  metric: "cpu_millicores" | "memory_bytes";
}) {
  if (history.length < 2)
    return <p className="tiny muted">Collecting samples…</p>;
  const max = Math.max(...history.map((s) => s[metric]), 1);
  const points = history
    .map(
      (s, i) =>
        `${(i / (history.length - 1)) * 240},${48 - (s[metric] / max) * 44}`,
    )
    .join(" ");
  return (
    <svg
      viewBox="0 0 240 52"
      className="advanced-sparkline"
      role="img"
      aria-label={`${metric === "cpu_millicores" ? "CPU" : "Memory"} hourly averages for observed samples in the last 24 hours`}
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <title>Hourly observed averages; missing hours are not zero usage.</title>
    </svg>
  );
}

export function ResourceInsights() {
  const { data, error, reload } = useApi(
    () => workspace.insights(),
    [],
    60_000,
  );
  const [budget, setBudget] = useState<string | null>(null),
    [threshold, setThreshold] = useState<number | null>(null);
  const { busy, run, feedback } = useAction(reload);
  if (error) return <p className="alert error">{error}</p>;
  if (!data) return <CardSkeleton />;
  return (
    <>
      <header className="page-head">
        <div>
          <p className="platform-eyebrow">RESOURCE & SPENDING INSIGHTS</p>
          <h1>Know what your apps use</h1>
          <p className="subtitle">
            Observed resource usage and recorded spending in your {data.currency} wallet.
          </p>
        </div>
        <Button onClick={reload}>Refresh</Button>
      </header>
      <section className="workspace-metrics">
        <div>
          <span className="platform-role">Charged this month</span>
          <strong>{money(data.charged, data.currency)}</strong>
        </div>
        <div>
          <span className="platform-role">Refunds this month</span>
          <strong>{money(data.refunds, data.currency)}</strong>
        </div>
        <div>
          <span className="platform-role">Current monthly plans</span>
          <strong>{money(data.configured_monthly, data.currency)}</strong>
        </div>
        <div>
          <span className="platform-role">Wallet balance</span>
          <strong>{money(data.wallet_balance, data.currency)}</strong>
        </div>
      </section>
      <p className="tiny muted">
        Application plans are counted once for their components. Current trial
        coverage is included. Renewal dates and plan changes affect future charges.
      </p>
      <section className="card stack">
        <h2>Monthly spending alert</h2>
        {feedback}
        {data.budget && (
          <div
            className={`platform-notice ${data.budget.state !== "within" ? "budget-warning" : ""}`}
            role="status"
          >
            <strong>
              {data.budget.used_percent.toFixed(1)}% of{" "}
              {money(data.budget.monthly_amount, data.currency)} charged
            </strong>
            <progress
              max={100}
              value={Math.min(100, data.budget.used_percent)}
              aria-label="Monthly budget used"
            />
            <p>
              {data.budget.state === "exceeded"
                ? "Your monthly budget has been exceeded."
                : data.budget.state === "warning"
                  ? "Your spending has crossed the warning threshold."
                  : "Spending is below your warning threshold."}
            </p>
          </div>
        )}
        <p className="muted">
          Alerts appear here when gross monthly charges cross your threshold.
          They do not stop services or change billing.
        </p>
        <form
          className="advanced-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(
              () =>
                workspace.budget(
                  budget ?? data.budget?.monthly_amount ?? "",
                  threshold ?? data.budget?.warning_percent ?? 80,
                  data.currency,
                ),
              "Spending alert saved",
            );
          }}
        >
          <label>
            Monthly budget ({data.currency})
            <input
              required
              type="number"
              min="0.01"
              max="999999999"
              step="0.01"
              value={budget ?? data.budget?.monthly_amount ?? ""}
              onChange={(e) => setBudget(e.target.value)}
            />
          </label>
          <label>
            Warn at (%)
            <input
              type="number"
              min={1}
              max={100}
              value={threshold ?? data.budget?.warning_percent ?? 80}
              onChange={(e) => setThreshold(Number(e.target.value))}
            />
          </label>
          <Button type="submit" loading={busy}>
            Save alert
          </Button>
          {data.budget && (
            <Button
              type="button"
              disabled={busy}
              onClick={() =>
                void run(() => workspace.removeBudget(data.currency), "Budget alert removed")
              }
            >
              Remove alert
            </Button>
          )}
        </form>
      </section>
      <h2 style={{ marginTop: 28 }}>Resources</h2>
      {!data.resources.length && (
        <p className="platform-notice">
          Deploy an application to start collecting resource samples.
        </p>
      )}
      <div className="platform-grid">
        {data.resources.map((r) => {
          const stale =
            !r.sample_at ||
            new Date(data.observed_at).getTime() -
              new Date(r.sample_at).getTime() >
              300_000;
          return (
            <article className="card stack" key={r.id}>
              <div className="row">
                <h3>
                  <Link
                    to={`/${r.kind === "service" ? "services" : "databases"}/${r.id}`}
                  >
                    {r.name}
                  </Link>
                </h3>
                <span className="grow" />
                <StatusBadge status={r.status} />
              </div>
              <p className="tiny muted">
                {r.kind === 'service' ? 'Included in application plan' : `${money(r.monthly_price, data.currency)}/month`} · {r.allocated_storage_gb} GB
                allocated storage
              </p>
              {r.kind === "service" ? (
                <>
                  <div className="row">
                    <strong>
                      {r.cpu_millicores === null
                        ? "—"
                        : `${r.cpu_millicores} mCPU`}
                    </strong>
                    <span className="grow" />
                    <strong>
                      {r.memory_bytes === null ? "—" : size(r.memory_bytes)}
                    </strong>
                  </div>
                  <Sparkline history={r.history} metric="cpu_millicores" />
                  <p className="tiny muted">
                    CPU trend ·{" "}
                    {stale
                      ? "Telemetry unavailable or older than 5 minutes"
                      : `Sampled ${relativeTime(r.sample_at!)}`}
                  </p>
                  <details>
                    <summary>Hourly measurements</summary>
                    <div className="advanced-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>mCPU</th>
                            <th>Memory</th>
                            <th>Samples</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.history.map((s) => (
                            <tr key={s.at}>
                              <td>{new Date(s.at).toLocaleTimeString()}</td>
                              <td>{s.cpu_millicores}</td>
                              <td>{size(s.memory_bytes)}</td>
                              <td>{s.samples}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </>
              ) : (
                <div className="stack">
                  <p>{r.cpu_millicores == null ? 'CPU unavailable' : `${r.cpu_millicores} mCPU`} · {r.memory_bytes == null ? 'Memory unavailable' : size(r.memory_bytes)}</p>
                  <p className={(r.database_health?.disk_used_percent ?? 0) >= 85 ? 'alert warn' : 'tiny muted'}>Storage: {r.database_health?.disk_used_percent == null ? 'unavailable' : `${r.database_health.disk_used_percent}% full`}</p>
                  <p className="tiny muted">Restarts: {r.database_health?.restarts ?? '—'} · Connections: {r.database_health?.connections ?? '—'}</p>
                  {r.database_health?.in_recovery && <p className="alert warn">PostgreSQL reports recovery / standby mode.</p>}
                  <p className="tiny muted">{stale ? 'Telemetry unavailable or older than 5 minutes' : `Sampled ${relativeTime(r.sample_at!)}`}</p>
                </div>
              )}
            </article>
          );
        })}
      </div>
      <section className="card" style={{ marginTop: 24 }}>
        <h2>Daily net charges this month</h2>
        <div className="advanced-table">
          <table>
            <thead>
              <tr>
                <th>Date (UTC)</th>
                <th>Charges minus refunds</th>
              </tr>
            </thead>
            <tbody>
              {data.daily_spend.map((d) => (
                <tr key={d.day}>
                  <td>{d.day}</td>
                  <td>{money(d.net, data.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!data.daily_spend.length && (
          <p className="muted">No charges or refunds recorded this month.</p>
        )}
      </section>
    </>
  );
}

export function ApplicationTemplates() {
  const { data, error } = useApi(() => workspace.templates(), []);
  const [selected, setSelected] = useState(""),
    [name, setName] = useState("myapp");
  const [configured, setConfigured] = useState<ConfiguredTemplate | null>(null);
  const { busy, run, feedback } = useAction(() => {});
  return (
    <>
      <header className="page-head">
        <div>
          <p className="platform-eyebrow">APPLICATION TEMPLATES</p>
          <h1>A starting point for your stack</h1>
          <p className="subtitle">
            Configure a deployment blueprint for your own application code.
          </p>
        </div>
      </header>
      {error && <p className="alert error">{error}</p>}
      {!data && !error && <CardSkeleton />}
      {feedback}
      <div className="platform-grid">
        {data?.map((t) => (
          <article
            className={`platform-tile ${selected === t.id ? "advanced-selected" : ""}`}
            key={t.id}
          >
            <span className="platform-role">
              Version {t.version} · {t.components} components
            </span>
            <h2>{t.name}</h2>
            <p className="muted">{t.description}</p>
            <p>
              {birr(t.monthly_etb)}/month at Starter compute + Dev database
              rates
            </p>
            <Button
              onClick={() => {
                setSelected(t.id);
                setConfigured(null);
              }}
            >
              Customize template
            </Button>
          </article>
        ))}
      </div>
      {selected && (
        <section className="card stack" style={{ marginTop: 24 }}>
          <form
            className="advanced-form"
            onSubmit={(e) => {
              e.preventDefault();
              void run(
                async () =>
                  setConfigured(await workspace.configure(selected, name)),
                "Blueprint validated and ready",
              );
            }}
          >
            <label>
              Application name
              <input
                required
                pattern="[a-z][a-z0-9-]{0,30}"
                maxLength={31}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <Button type="submit" loading={busy}>
              Generate blueprint
            </Button>
          </form>
          {configured && (
            <>
              <h2>Your {configured.filename}</h2>
              <textarea
                className="advanced-code"
                aria-label="Generated deployment blueprint"
                readOnly
                value={configured.content}
                rows={18}
                onFocus={(e) => e.target.select()}
              />
              <Button
                onClick={() => {
                  const url = URL.createObjectURL(
                    new Blob([configured.content], { type: "text/yaml" }),
                  );
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = configured.filename;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                }}
              >
                Download {configured.filename}
              </Button>
              <ol>
                {configured.steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ol>
              {configured.notes.map((n) => (
                <p className="tiny muted" key={n}>
                  {n}
                </p>
              ))}
              <Link className="btn primary" to="/services/new">
                Connect repository and review →
              </Link>
            </>
          )}
        </section>
      )}
    </>
  );
}
